import React, { useState, useMemo } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Building2, Landmark, Briefcase } from 'lucide-react';
import { clients } from '../mock';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';

const Clients = () => {
  const { language } = useLanguage();
  const clientsByType = useMemo(() => ({
    government: clients.filter(c => c.type === 'government'),
    finance: clients.filter(c => c.type === 'finance'),
    enterprise: clients.filter(c => c.type === 'enterprise')
  }), []);

  const [selectedCategory, setSelectedCategory] = useState('all');

  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
      slidesToScroll: 1
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );

  // Combine and filter clients for carousel
  const allClients = useMemo(() => {
    const list = [...clientsByType.government, ...clientsByType.finance, ...clientsByType.enterprise];
    if (selectedCategory === 'all') return list;
    return list.filter(c => c.category === selectedCategory);
  }, [clientsByType, selectedCategory]);

  const getClientIcon = (type) => {
    switch (type) {
      case 'government':
        return Landmark;
      case 'finance':
        return Briefcase;
      case 'enterprise':
        return Building2;
      default:
        return Building2;
    }
  };

  const getClientCategory = (type) => {
    if (language === 'id') {
      switch (type) {
        case 'government':
          return 'Pemerintah';
        case 'finance':
          return 'Keuangan';
        case 'enterprise':
          return 'Enterprise';
        default:
          return 'Perusahaan';
      }
    } else {
      switch (type) {
        case 'government':
          return 'Government';
        case 'finance':
          return 'Finance';
        case 'enterprise':
          return 'Enterprise';
        default:
          return 'Company';
      }
    }
  };

  const clientLogos = {
    "Kementerian ESDM": "/images/clients/central%20government/Kementerian%20ESDM.png",
    "BSSN": "/images/clients/central%20government/BSSN%20(Badan%20Siber%20dan%20Sandi%20Negara).png",
    "KPU (Komisi Pemilihan Umum)": "/images/clients/central%20government/KPU%20(Komisi%20Pemilihan%20Umum).png",
    "Perpustakaan Nasional RI": "/images/clients/central%20government/Perpustakaan%20Nasional%20Republik%20Indonesia.png",
    "KPK": "/images/clients/central%20government/KPK%20(Komisi%20Pemberantasan%20Korupsi).png",
    "PERURI": "/images/clients/central%20government/PERURI.png",
    "BSrE": "/images/clients/central%20government/Balai%20Sertifikasi%20Elektronik%20(BSrE).png",
    "Kementerian PUPR": "/images/clients/central%20government/Kementerian%20PUPR.png",
    "Badan Otorita Borobudur": "/images/clients/central%20government/Badan%20Otorita%20Borobudur.png",
    "Perhutani": "/images/clients/central%20government/Perhutani%20(Perum%20Perhutani).png",
    "PT Bukit Asam": "/images/clients/central%20government/Bukit%20Asam.png",
    "Hutama Karya (HK)": "/images/clients/central%20government/Hutama%20Karya%20(HK).png",
    "Mitratel": "/images/clients/central%20government/Mitratel.png",
    "Infomedia": "/images/clients/central%20government/Infomedia.png",
    "Bapenda Jakarta": "/images/clients/local%20government/Bapenda%20Jakarta.png",
    "Pemkab Kutai Kartanegara": "/images/clients/local%20government/Pemerintah%20Kabupaten%20Kutai%20Kartanegara.png",
    "Pemkab Jembrana": "/images/clients/local%20government/Pemerintah%20Kabupaten%20Jembrana.png",
    "Pemkab Buleleng": "/images/clients/local%20government/Pemerintah%20Kabupaten%20Buleleng.png",
    "Pemkab Badung": "/images/clients/local%20government/Pemerintah%20Kabupaten%20Badung.png",
    "Pemkab Tabanan": "/images/clients/local%20government/Pemerintah%20Kabupaten%20Tabanan.png",
    "Pemkab Karangasem": "/images/clients/local%20government/Pemerintah%20Kabupaten%20Karangasem.png",
    "Pemkab Klungkung": "/images/clients/local%20government/Pemerintah%20Kabupaten%20Klungkung.png",
    "Pemkab Bekasi": "/images/clients/local%20government/Pemerintah%20Kabupaten%20Bekasi.png",
    "Toyota Indonesia": "/images/clients/private%20sector/Toyota%20Indonesia.png",
    "GOTO (Gojek Tokopedia)": "/images/clients/private%20sector/GOTO%20(Gojek%20Tokopedia).png",
    "PrivyID": "/images/clients/private%20sector/PrivyID.png",
    "Moratelindo": "/images/clients/private%20sector/Moratelindo.png",
    "Sinarmas": "/images/clients/private%20sector/Smart%20Agribusiness%20and%20Food%20(Sinarmas%20Group).png",
    "Bara Perkasa Investama": "/images/clients/private%20sector/Bara%20Perkasa%20Investama.png",
    "Cemindo Gemilang": "/images/clients/private%20sector/Cemindo%20Gemilang.png"
  };

  return (
    <section id="clients" className="py-24 bg-gradient-to-b from-white via-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-[1.15]">
            <span className="bg-gradient-to-r from-yellow-600 to-red-600 bg-clip-text text-transparent pb-2">
              {t(language, 'clients.ourClients')}
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t(language, 'clients.description')}
          </p>
        </div>

        {/* Category Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <button
            onClick={() => setSelectedCategory('central')}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${selectedCategory === 'central' ? 'bg-yellow-600 text-white' : 'bg-yellow-50 text-yellow-700'}`}
            aria-pressed={selectedCategory === 'central'}
          >
            {language === 'id' ? 'Pusat' : 'Central Government'}
          </button>
          <button
            onClick={() => setSelectedCategory('local')}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${selectedCategory === 'local' ? 'bg-yellow-600 text-white' : 'bg-yellow-50 text-yellow-700'}`}
            aria-pressed={selectedCategory === 'local'}
          >
            {language === 'id' ? 'Daerah' : 'Local Government'}
          </button>
          <button
            onClick={() => setSelectedCategory('private')}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${selectedCategory === 'private' ? 'bg-yellow-600 text-white' : 'bg-yellow-50 text-yellow-700'}`}
            aria-pressed={selectedCategory === 'private'}
          >
            {language === 'id' ? 'Swasta' : 'Private Sector'}
          </button>
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${selectedCategory === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-800'}`}
            aria-pressed={selectedCategory === 'all'}
          >
            {language === 'id' ? 'Semua' : 'All'}
          </button>
        </div>

        {/* Clients Carousel */}
        <div className="relative mb-20">
          <div className="overflow-hidden pb-6 -mb-6" ref={emblaRef}>
            <div className="flex -ml-4">
              {allClients.map((client) => {
                const Icon = getClientIcon(client.type);
                return (
                  <div
                    key={`${client.type}-${client.name}`}
                    className="flex-[0_0_100%] min-w-0 sm:flex-[0_0_50%] md:flex-[0_0_40%] lg:flex-[0_0_25%] pl-4"
                  >
                    <div className="bg-white rounded-2xl p-7 shadow-md border border-gray-100 hover:shadow-2xl hover:border-yellow-300 transition-all duration-300 h-full group">
                      <div className="flex items-start mb-5">
                        <div className="w-11 h-11 bg-gradient-to-br from-yellow-50 to-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                          <Icon className="text-yellow-600" size={22} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="inline-block px-2.5 py-1 bg-yellow-50 text-yellow-600 text-xs font-semibold rounded-full mb-2.5">
                            {getClientCategory(client.type)}
                          </span>
                          <h3 className="font-bold text-gray-900 text-sm leading-snug whitespace-normal break-words min-h-[2.5rem]">
                            {client.name}
                          </h3>
                        </div>
                      </div>
                      <div className="w-full h-24 flex items-center justify-center p-2">
                        {clientLogos[client.name] ? (
                          <div className="w-[120px] h-full flex items-center justify-center">
                            <img
                              src={clientLogos[client.name]}
                              alt={`Logo ${client.name}`}
                              loading="lazy"
                              decoding="async"
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs text-center px-3 py-2">
                            Logo {client.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation Buttons Removed */}
        </div>

        {/* Client Categories Overview */}
        {/* <div className="grid md:grid-cols-3 gap-8 mb-20 mt-12">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-md border border-gray-100 hover:shadow-xl hover:border-yellow-200 transition-all duration-300 group text-center">
            <div className="w-14 h-14 bg-gradient-to-br from-yellow-50 to-red-100 rounded-xl flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform duration-300">
              <Landmark className="w-7 h-7 text-yellow-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Instansi Pemerintah</h3>
            <p className="text-4xl font-bold bg-gradient-to-r from-yellow-600 to-red-600 bg-clip-text text-transparent mb-2">{clientsByType.government.length}+</p>
            <p className="text-gray-600 text-sm font-medium">Kementerian & Pemda</p>
          </div>
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-md border border-gray-100 hover:shadow-xl hover:border-yellow-200 transition-all duration-300 group text-center">
            <div className="w-14 h-14 bg-gradient-to-br from-yellow-50 to-red-100 rounded-xl flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform duration-300">
              <Briefcase className="w-7 h-7 text-yellow-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Lembaga Keuangan</h3>
            <p className="text-4xl font-bold bg-gradient-to-r from-yellow-600 to-red-600 bg-clip-text text-transparent mb-2">{clientsByType.finance.length}+</p>
            <p className="text-gray-600 text-sm font-medium">Perbankan & Institusi</p>
          </div>
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-md border border-gray-100 hover:shadow-xl hover:border-yellow-200 transition-all duration-300 group text-center">
            <div className="w-14 h-14 bg-gradient-to-br from-yellow-50 to-red-100 rounded-xl flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform duration-300">
              <Building2 className="w-7 h-7 text-yellow-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Perusahaan</h3>
            <p className="text-4xl font-bold bg-gradient-to-r from-yellow-600 to-red-600 bg-clip-text text-transparent mb-2">{clientsByType.enterprise.length}+</p>
            <p className="text-gray-600 text-sm font-medium">BUMN & Swasta</p>
          </div>
        </div> */}

        {/* Trust Indicators */}
        {/* <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 pt-12 border-t border-gray-200">
          <div className="text-center p-8 bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-100 shadow-md hover:shadow-lg hover:border-yellow-200 transition-all duration-300 group">
            <div className="text-5xl font-bold bg-gradient-to-r from-yellow-600 to-red-600 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">
              98%
            </div>
            <div className="text-gray-700 text-sm font-semibold">Kepuasan Klien</div>
          </div>
          <div className="text-center p-8 bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-100 shadow-md hover:shadow-lg hover:border-yellow-200 transition-all duration-300 group">
            <div className="text-5xl font-bold bg-gradient-to-r from-yellow-600 to-red-600 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">
              200+
            </div>
            <div className="text-gray-700 text-sm font-semibold">Klien Aktif</div>
          </div>
          <div className="text-center p-8 bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-100 shadow-md hover:shadow-lg hover:border-yellow-200 transition-all duration-300 group">
            <div className="text-5xl font-bold bg-gradient-to-r from-yellow-600 to-red-600 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">
              500+
            </div>
            <div className="text-gray-700 text-sm font-semibold">Proyek Sukses</div>
          </div>
          <div className="text-center p-8 bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-100 shadow-md hover:shadow-lg hover:border-yellow-200 transition-all duration-300 group">
            <div className="text-5xl font-bold bg-gradient-to-r from-yellow-600 to-red-600 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">
              5+
            </div>
            <div className="text-gray-700 text-sm font-semibold">Tahun Pengalaman</div>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default Clients;

